import { Product } from '../types';
import { getAvailableStock } from './variant';

/**
 * Check if a product is out of stock
 * @param product - The product to check
 * @returns boolean - true if out of stock, false if in stock
 */
export const isOutOfStock = (product: Product): boolean => {
  if (!product) return true;
  
  // For variable products, check if any variant has stock
  if (product.productType === 'variable' && product.variants && product.variants.length > 0) {
    // Check if any variant has stock > 0
    const hasStock = product.variants.some(variant => (variant.stockQty ?? 0) > 0);
    return !hasStock;
  }
  
  // For simple products, use getAvailableStock (standardized logic: totalStock ?? stockQty)
  const availableStock = getAvailableStock(product);
  return availableStock <= 0;
};

/**
 * Get the total available stock quantity for a product
 * For variable products, returns the sum of all variant stock
 * For simple products, returns the product-level stock
 * 
 * @param product - The product to check
 * @returns number - The total available stock quantity
 */
export const getProductStock = (product: Product): number => {
  if (!product) return 0;
  
  // For variable products, return the total stock across all variants
  if (product.productType === 'variable' && product.variants && product.variants.length > 0) {
    return product.variants.reduce((total, variant) => total + (variant.stockQty ?? 0), 0);
  }
  
  // For simple products, use getAvailableStock (standardized logic)
  return getAvailableStock(product);
};

/**
 * Get stock status text for display
 * @param product - The product to check
 * @returns string - Stock status text
 */
export const getStockStatusText = (product: Product): string => {
  if (isOutOfStock(product)) {
    return 'Out of Stock';
  }
  
  const stock = getProductStock(product);
  if (stock < 10) {
    return 'Limited Stock';
  }
  
  return 'In Stock';
};

/**
 * Get stock status color for UI
 * @param product - The product to check
 * @returns string - CSS color class
 */
export const getStockStatusColor = (product: Product): string => {
  if (isOutOfStock(product)) {
    return 'text-red-600 bg-red-50';
  }
  
  const stock = getProductStock(product);
  if (stock < 10) {
    return 'text-orange-600 bg-orange-50';
  }
  
  return 'text-green-600 bg-green-50';
};

/**
 * Get the maximum stock quantity available for any single variant
 * Useful for cart quantity limits
 * @param product - The product to check
 * @returns number - The maximum stock quantity for any variant
 */
export const getMaxVariantStock = (product: Product): number => {
  if (!product) return 0;
  
  // For variable products, return the maximum stock among all variants
  if (product.productType === 'variable' && product.variants && product.variants.length > 0) {
    return Math.max(...product.variants.map(variant => variant.stockQty ?? 0));
  }
  
  // For simple products, return the product stock
  return getProductStock(product);
};
